import { forwardRef, useEffect, useState } from 'react';
import { AlertTriangle, RefreshCw, WifiOff, Clock } from 'lucide-react';

const formatCountdown = (ms) => {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes > 0) {
    return `${minutes}m ${seconds.toString().padStart(2, '0')}s`;
  }
  return `${seconds}s`;
};

const useCountdown = (initialMs) => {
  const [remainingMs, setRemainingMs] = useState(
    typeof initialMs === 'number' && initialMs > 0 ? initialMs : 0
  );

  useEffect(() => {
    if (typeof initialMs !== 'number' || initialMs <= 0) {
      setRemainingMs(0);
      return undefined;
    }

    const startedAt = Date.now();
    setRemainingMs(initialMs);

    const tick = () => {
      const elapsed = Date.now() - startedAt;
      const next = Math.max(0, initialMs - elapsed);
      setRemainingMs(next);
      if (next <= 0) {
        clearInterval(intervalId);
      }
    };

    const intervalId = setInterval(tick, 1000);
    return () => clearInterval(intervalId);
  }, [initialMs]);

  return remainingMs;
};

const VARIANTS = {
  AI_RATE_LIMITED: {
    title: "You've reached the AI search limit.",
    body: 'Please wait a moment before trying again.',
    Icon: Clock,
    iconWrap: 'bg-amber-100 text-amber-700',
    border: 'border-amber-200',
    bg: 'bg-amber-50',
  },
  NETWORK_ERROR: {
    title: "We couldn't complete your search.",
    body: 'Check your connection and try again.',
    Icon: WifiOff,
    iconWrap: 'bg-blue-100 text-blue-700',
    border: 'border-blue-200',
    bg: 'bg-blue-50',
  },
  GENERIC: {
    title: 'Something went wrong.',
    body: 'Please try again.',
    Icon: AlertTriangle,
    iconWrap: 'bg-red-100 text-red-700',
    border: 'border-red-200',
    bg: 'bg-red-50',
  },
};

const pickVariant = (code) => {
  if (code === 'AI_RATE_LIMITED') return VARIANTS.AI_RATE_LIMITED;
  if (code === 'NETWORK_ERROR') return VARIANTS.NETWORK_ERROR;
  return VARIANTS.GENERIC;
};

const AISearchError = forwardRef(({ error, onRetry }, headingRef) => {
  const code = error?.code;
  const variant = pickVariant(code);
  const isRateLimited = code === 'AI_RATE_LIMITED';
  const remainingMs = useCountdown(isRateLimited ? error?.retryAfterMs : 0);
  const retryDisabled = isRateLimited && remainingMs > 0;

  const handleRetry = () => {
    if (retryDisabled) return;
    if (typeof onRetry === 'function') onRetry();
  };

  const { Icon } = variant;

  return (
    <div
      role="alert"
      aria-live="assertive"
      className={`max-w-xl mx-auto my-6 p-5 sm:p-6 rounded-xl border ${variant.border} ${variant.bg}`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${variant.iconWrap}`}
        >
          <Icon className="w-5 h-5" aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <h2
            ref={headingRef}
            tabIndex={-1}
            className="text-base font-semibold text-gray-900 focus:outline-none"
          >
            {variant.title}
          </h2>
          <p className="text-sm text-gray-700 mt-1">{variant.body}</p>

          {isRateLimited && remainingMs > 0 ? (
            <p
              className="text-xs text-gray-600 mt-2"
              aria-live="polite"
            >
              You can try again in{' '}
              <span className="font-semibold">
                {formatCountdown(remainingMs)}
              </span>
              .
            </p>
          ) : null}

          <div className="mt-4">
            <button
              type="button"
              onClick={handleRetry}
              disabled={retryDisabled}
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium min-h-[44px] transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                retryDisabled
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed focus:ring-gray-300'
                  : 'bg-primary text-white hover:bg-primary/90 focus:ring-primary/40'
              }`}
            >
              <RefreshCw className="w-4 h-4" aria-hidden="true" />
              {retryDisabled
                ? `Retry in ${formatCountdown(remainingMs)}`
                : 'Try again'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});

AISearchError.displayName = 'AISearchError';

export default AISearchError;
